import { useEffect, useState } from 'react'
import { AlertTriangle, Loader2 } from 'lucide-react'
import type { BrokerConnectionDto, DeletionImpactDto } from '@apna/shared'
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog'
import { api } from '@/lib/api'
import { cn } from '@/lib/utils'

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`

/**
 * The exact phrase that has to be typed, in full caps.
 *
 * It NAMES THE COUNTS. "Delete my broker account with 11 trades" is a
 * sentence a person can check against what they believe is true — if they
 * thought the account had two, the phrase is where they find out otherwise.
 * A generic "yes" cannot do that, and a checkbox is worse: it is one click
 * from the same muscle memory that opened the menu.
 */
export function confirmationPhrase(trades: number, deployments: number): string {
  const base = `Delete my broker account with ${plural(trades, 'trade', 'trades')}`
  const full = deployments > 0 ? `${base} and ${plural(deployments, 'strategy', 'strategies')}` : base
  return full.toUpperCase()
}

/**
 * Deleting a broker account — ROADMAP 2.x, after trading-journal's.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * AN OPEN POSITION IS A REFUSAL, NOT A WARNING
 *
 * Deleting an account does NOT close what it holds. It removes the one record
 * that knows the position exists, leaving real money in the market with
 * nothing watching it — the premium monitor's stops included. So an account
 * with anything live cannot be deleted at all, and the dialog says which
 * account and how many rather than offering a way through.
 *
 * The server refuses it too. This one read its counts a moment ago and a leg
 * can fill between that read and the click; the 409 is what survives the
 * race. Same shape as a pre-check in front of a unique index — the sentence
 * here is for a person, the server's check is the one that is true.
 * ─────────────────────────────────────────────────────────────────────────────
 */
export function DeleteConnectionDialog({
  connection,
  onClose,
  onDeleted,
}: {
  connection: BrokerConnectionDto | null
  onClose: () => void
  onDeleted: () => void
}) {
  const [impact, setImpact] = useState<DeletionImpactDto | null>(null)
  const [loading, setLoading] = useState(true)
  const [typed, setTyped] = useState('')
  const [deleting, setDeleting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!connection) return
    setTyped('')
    setError(null)
    setImpact(null)
    setLoading(true)
    void api
      .get<DeletionImpactDto>(`/broker/connections/${connection.id}/deletion-impact`)
      .then(setImpact)
      .catch((e: unknown) => setError(e instanceof Error ? e.message : 'Could not check this account.'))
      .finally(() => setLoading(false))
  }, [connection])

  if (!connection) return null

  const name = connection.label || connection.clientCode
  const blocked = (impact?.openTrades ?? 0) > 0 || (impact?.workingOrders ?? 0) > 0
  const phrase = confirmationPhrase(impact?.trades ?? 0, impact?.deployments ?? 0)
  const matches = typed.trim().toUpperCase() === phrase
  const canDelete = !loading && !error && impact !== null && !blocked && matches && !deleting

  const confirm = async () => {
    if (!canDelete) return
    setDeleting(true)
    setError(null)
    try {
      await api.del(`/broker/connections/${connection.id}`)
      onDeleted()
      onClose()
    } catch (e: unknown) {
      // Including the server's own 409, which is the one that wins a race.
      setError(e instanceof Error ? e.message : 'Could not delete this account.')
    } finally {
      setDeleting(false)
    }
  }

  return (
    <Dialog open onOpenChange={(o) => { if (!o && !deleting) onClose() }}>
      <DialogContent className="gap-0 overflow-hidden p-0 sm:max-w-[520px]" showCloseButton={!deleting}>
        <header className="border-b border-border px-5 py-3.5">
          <DialogTitle className="text-[15px] font-semibold">Delete this broker account?</DialogTitle>
          <DialogDescription className="sr-only">
            Confirm removal of {name} and everything recorded against it.
          </DialogDescription>
        </header>

        <div className="p-5">
          {loading && (
            <p className="flex items-center gap-2 py-2 text-[12px] text-muted-foreground">
              <Loader2 size={14} className="animate-spin" /> Checking what this account is linked to…
            </p>
          )}

          {!loading && error && !impact && (
            <p role="alert" className="rounded border border-destructive/20 bg-destructive/5 p-2.5 text-[12px] text-destructive">
              {error}
            </p>
          )}

          {!loading && impact && blocked && (
            <div role="alert" data-testid="delete-blocked"
              className="flex items-start gap-2 rounded-lg border border-destructive/25 bg-destructive/5 p-3">
              <AlertTriangle size={15} className="mt-0.5 shrink-0 text-destructive" />
              <p className="text-[12px] text-destructive">
                <span className="font-semibold">{name}</span>{' '}
                {impact.openTrades > 0 ? (
                  <>still has {plural(impact.openTrades, 'open trade', 'open trades')}. Close{' '}
                    {impact.openTrades === 1 ? 'it' : 'them'} before deleting this broker account.</>
                ) : (
                  <>has {plural(impact.workingOrders, 'order', 'orders')} still working at the broker. Wait for{' '}
                    {impact.workingOrders === 1 ? 'it' : 'them'} to settle before deleting this broker account.</>
                )}
              </p>
            </div>
          )}

          {!loading && impact && !blocked && (
            <div className="grid gap-4">
              <p className="text-[12px] text-muted-foreground">
                Deleting <span className="font-semibold text-foreground">{name}</span> will also remove{' '}
                <span className="font-medium text-foreground">{plural(impact.trades, 'trade', 'trades')}</span>
                {impact.deployments > 0 && (
                  <> and <span className="font-medium text-foreground">{plural(impact.deployments, 'strategy', 'strategies')}</span></>
                )}
                . This action can&apos;t be undone.
              </p>

              <label className="grid gap-1.5 text-[11px] text-muted-foreground">
                To confirm, type <span className="font-medium text-foreground">&ldquo;{phrase}&rdquo;</span>
                <input
                  value={typed}
                  onChange={(e) => setTyped(e.target.value)}
                  placeholder={phrase}
                  autoComplete="off"
                  spellCheck={false}
                  disabled={deleting}
                  data-testid="delete-confirm-input"
                  aria-invalid={typed.length > 0 && !matches}
                  className={cn(
                    'h-9 w-full rounded-md border bg-background px-3 text-sm outline-none focus:ring-2 focus:ring-primary/15',
                    typed.length > 0 && !matches ? 'border-destructive' : 'border-border focus:border-primary',
                  )}
                />
              </label>

              {error && (
                <p role="alert" className="rounded border border-destructive/20 bg-destructive/5 p-2 text-[11px] text-destructive">
                  {error}
                </p>
              )}
            </div>
          )}
        </div>

        <footer className="flex justify-end gap-2 border-t border-border px-5 py-3">
          <button type="button" disabled={deleting} onClick={onClose}
            className="rounded-md border border-border px-3 py-2 text-[12px] font-medium disabled:opacity-40">
            Cancel
          </button>
          <button type="button" disabled={!canDelete} onClick={() => void confirm()} data-testid="delete-confirm"
            className="inline-flex items-center gap-2 rounded-md bg-destructive px-4 py-2 text-[12px] font-semibold text-white disabled:opacity-40">
            {deleting && <Loader2 size={13} className="animate-spin" />}
            Delete broker account
          </button>
        </footer>
      </DialogContent>
    </Dialog>
  )
}
